"use client";

import { useEffect, useId, useRef, useState } from "react";
import Script from "next/script";
import { CheckCircleIcon, PhoneIcon } from "@heroicons/react/24/outline";
import { TrackingEvents } from "@/lib/analytics";
import { useFormTracking } from "@/hooks/useFormTracking";
import {
  buildBrowserAttributionEnvelope,
  createSubmissionId,
  setBrowserAttributionLeadId,
} from "@/lib/ad-attribution";
import { sanitizeConsultationContext } from "@/lib/consultation";
import { suppressDiagnosticNudge } from "@/lib/diagnostic-nudge";
import { useTurnstile } from "@/hooks/useTurnstile";
import { FieldLabel, InlineError, fieldClassName } from "./DesignSystem";
import { focusFirstFormError } from "./formValidationFocus";
import { submitCrmForm } from "@/lib/form-submission";

const WATER_ISSUES = [
  ["", "Select a concern"],
  ["hard-water", "Hard water or scale"],
  ["drinking-water", "Drinking-water taste or quality"],
  ["staining-issues", "Staining, discolouration, or odour"],
  ["well-water", "Well-water safety or bacteria concern"],
  ["chlorine", "Chlorine taste or odour"],
  ["not-sure", "Not sure yet"],
];
const WATER_ISSUE_VALUES = new Set(WATER_ISSUES.map(([value]) => value));

function initialFormData(mode, initialWaterIssue = "") {
  return {
    companyName: "",
    firstName: "",
    phone: "",
    waterIssue: WATER_ISSUE_VALUES.has(initialWaterIssue) ? initialWaterIssue : "",
    note: "",
    mode,
  };
}

export function QuickAssessment({
  mode = "residential",
  service = "general",
  pagePath,
  placement = "inline",
  audience,
  city,
  contentId,
  ctaVariant = "quick-assessment",
  funnelVariant,
  responsiveVerification = false,
  diagnostic,
  submitLabel,
  initialWaterIssue = "",
}) {
  const id = useId();
  const formRef = useRef(null);
  const submissionIdRef = useRef(null);
  const submittingRef = useRef(false);
  const [formData, setFormData] = useState(() => initialFormData(mode, initialWaterIssue));
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: "idle", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldLoadTurnstile, setShouldLoadTurnstile] = useState(false);
  const { turnstileRef, turnstileToken, resetTurnstile, handleScriptLoad } = useTurnstile({
    responsive: responsiveVerification,
  });

  const context = sanitizeConsultationContext({
    mode,
    service,
    pagePath,
    placement,
    audience,
    city,
    contentId,
    ctaVariant,
    diagnostic,
  });
  const formType = mode === "business" ? "business_quick_assessment" : mode === "general" ? "quick_contact" : "home_quick_assessment";
  const formVariant = funnelVariant || `${mode}-${service}-${placement}`;
  const trackingDimensions = {
    form_type: formType,
    form_variant: formVariant,
    program_interest: service,
    placement: context.placement,
    cta_variant: context.ctaVariant,
    page_path: context.pagePath,
  };
  const trackFormStart = useFormTracking({ formRef, dimensions: trackingDimensions });

  useEffect(() => {
    const form = formRef.current;
    if (!form || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadTurnstile(true);
          observer.disconnect();
        }
      },
      { rootMargin: "240px" },
    );
    observer.observe(form);
    return () => observer.disconnect();
  }, []);

  const turnstileError = turnstileToken ? null : errors.turnstile;

  function handleChange(event) {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    if (errors[name]) {
      setErrors((current) => {
        const next = { ...current };
        delete next[name];
        return next;
      });
    }
  }

  function validate() {
    const nextErrors = {};
    if (mode === "business" && !formData.companyName.trim()) {
      nextErrors.companyName = "Enter your company name.";
    }
    if (!formData.firstName.trim()) nextErrors.firstName = "Enter your first name.";
    const phoneDigits = formData.phone.replace(/\D/g, "");
    if (!formData.phone.trim()) nextErrors.phone = "Enter your phone number.";
    else if (phoneDigits.length < 10) nextErrors.phone = "Enter a valid phone number with at least 10 digits.";
    if (!turnstileToken) nextErrors.turnstile = "Complete the human verification check.";
    return nextErrors;
  }

  function trackStart() {
    suppressDiagnosticNudge(window);
    trackFormStart();
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submittingRef.current) return;
    trackStart();
    const nextErrors = validate();
    TrackingEvents.trackFormStep("submit_attempt", {
        ...trackingDimensions,
        has_water_issue: Boolean(formData.waterIssue),
    });
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setStatus({ type: "error", message: "Please check the highlighted fields and try again." });
      TrackingEvents.trackFormStep("validation_error", {
          ...trackingDimensions,
          error_fields: Object.keys(nextErrors).join(","),
          error_count: Object.keys(nextErrors).length,
      });
      requestAnimationFrame(() => focusFirstFormError(formRef.current));
      return;
    }

    submittingRef.current = true;
    try {
      setIsSubmitting(true);
      setStatus({ type: "idle", message: "" });
      submissionIdRef.current ||= createSubmissionId();
      const attribution = buildBrowserAttributionEnvelope({
        submissionId: submissionIdRef.current,
        formType,
      });

      const endpoint = mode === "general" ? "/api/crm/submit-contact" : "/api/crm/submit-assessment";
      const payload = mode === "general"
      ? {
          first_name: formData.firstName,
          phone: formData.phone,
          customer_note: formData.note,
          type: "quick-contact",
          contact_intent: "general",
          source: attribution?.submissionTouch?.source,
          attribution,
          consultation_context: context,
          turnstile_token: turnstileToken,
        }
      : {
          status: "new",
          type: mode === "business" ? "business-quick-assessment" : "home-quick-assessment",
          company_name: mode === "business" ? formData.companyName : undefined,
          first_name: formData.firstName,
          phone: formData.phone,
          water_issues: formData.waterIssue ? [formData.waterIssue] : [],
          program_interest: service,
          source: attribution?.submissionTouch?.source,
          attribution,
          consultation_context: context,
          turnstile_token: turnstileToken,
        };

      const { response, result } = await submitCrmForm(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok || !result.success) {
        if (result.fieldErrors) {
          setErrors((current) => ({
            ...current,
            ...(result.fieldErrors.company_name ? { companyName: result.fieldErrors.company_name } : {}),
            ...(result.fieldErrors.first_name ? { firstName: result.fieldErrors.first_name } : {}),
            ...(result.fieldErrors.phone ? { phone: result.fieldErrors.phone } : {}),
          }));
          requestAnimationFrame(() => focusFirstFormError(formRef.current));
        }
        const submissionError = new Error(result.error || "Your request could not be submitted.");
        submissionError.code = result.code;
        throw submissionError;
      }
      const leadId = result.data?.leadId || result.data?.project?.projectId || result.data?.project?.id;
      if (!leadId) throw new Error("Lead creation was not confirmed.");

      try { setBrowserAttributionLeadId(leadId); }
      catch (storageError) { console.error("Confirmed lead storage failed:", storageError); }
      try {
        const successDimensions = { form_type: formType, form_variant: formVariant, placement: context.placement, cta_variant: context.ctaVariant, page_path: context.pagePath };
        if (mode === "business") TrackingEvents.trackBusinessAssessment({ formId: leadId, ...successDimensions });
        else if (mode === "general") TrackingEvents.trackContactForm({ formId: leadId, ...successDimensions });
        else TrackingEvents.trackHomeAssessment({ formId: leadId, programInterest: service, updated: result.updated || false, ...successDimensions });
      } catch (trackingError) {
        console.error("Confirmed lead tracking failed:", trackingError);
      }

      submissionIdRef.current = null;
      setFormData(initialFormData(mode, initialWaterIssue));
      setErrors({});
      setStatus({
        type: "success",
        message: "Thanks — your request is in. We’ll call to confirm your needs and, when appropriate, arrange a visit. If you have them, keep your water test results, address, and current equipment details handy.",
      });
      resetTurnstile();
    } catch (error) {
      setStatus({
        type: "error",
        message: error?.code === "submission_unconfirmed"
          ? error.message
          : error instanceof Error && error.message
          ? `${error.message} Try again, or call 289-748-9666 during business hours.`
          : "We could not submit your request. Try again, or call 289-748-9666 during business hours.",
      });
      resetTurnstile();
      try {
        TrackingEvents.trackFormStep("submit_error", {
          ...trackingDimensions,
          error_type: error instanceof Error ? "submission_failed" : "unknown",
        });
      } catch (trackingError) {
        console.error("Submission error tracking failed:", trackingError);
      }
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }

  if (status.type === "success") {
    return (
      <div className="min-w-0 max-w-full rounded-[1.375rem] border border-emerald-200 bg-emerald-50 p-6 sm:p-8" role="status" aria-live="polite">
        <CheckCircleIcon className="h-8 w-8 text-water-teal" aria-hidden="true" />
        <h3 className="mt-4 text-xl font-semibold text-water-ink">Request received</h3>
        <p className="mt-2 text-base leading-7 text-water-muted">{status.message}</p>
        <a href="tel:+12897489666" className="mt-5 inline-flex min-h-11 items-center gap-2 font-semibold text-water-blue hover:text-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2">
          <PhoneIcon className="h-5 w-5" aria-hidden="true" />
          Call 289-748-9666
        </a>
      </div>
    );
  }

  return (
    <>
      {shouldLoadTurnstile ? (
        <Script
          src="https://challenges.cloudflare.com/turnstile/v0/api.js"
          async
          defer
          strategy="afterInteractive"
          onLoad={handleScriptLoad}
          onReady={handleScriptLoad}
        />
      ) : null}
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        onFocusCapture={() => {
          setShouldLoadTurnstile(true);
          trackStart();
        }}
        className="min-w-0 max-w-full space-y-5"
        noValidate
      >
        <p className="text-sm leading-6 text-water-muted">Only the fields marked with <span className="text-red-700">*</span> are required.</p>

        {mode === "business" ? (
          <div>
            <FieldLabel htmlFor={`${id}-company`}>Company name</FieldLabel>
            <input id={`${id}-company`} name="companyName" autoComplete="organization" value={formData.companyName} onChange={handleChange} required aria-invalid={Boolean(errors.companyName)} aria-describedby={errors.companyName ? `${id}-company-error` : undefined} className={fieldClassName} />
            <InlineError id={`${id}-company-error`}>{errors.companyName}</InlineError>
          </div>
        ) : null}

        <div className="grid min-w-0 gap-5 sm:grid-cols-2">
          <div className="min-w-0">
            <FieldLabel htmlFor={`${id}-first-name`}>First name</FieldLabel>
            <input id={`${id}-first-name`} name="firstName" autoComplete="given-name" value={formData.firstName} onChange={handleChange} required aria-invalid={Boolean(errors.firstName)} aria-describedby={errors.firstName ? `${id}-first-name-error` : undefined} className={fieldClassName} />
            <InlineError id={`${id}-first-name-error`}>{errors.firstName}</InlineError>
          </div>
          <div className="min-w-0">
            <FieldLabel htmlFor={`${id}-phone`}>Phone</FieldLabel>
            <input id={`${id}-phone`} name="phone" type="tel" inputMode="tel" autoComplete="tel" value={formData.phone} onChange={handleChange} placeholder="e.g. 289-748-9666…" required aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? `${id}-phone-error` : undefined} className={fieldClassName} />
            <InlineError id={`${id}-phone-error`}>{errors.phone}</InlineError>
          </div>
        </div>

        {mode === "residential" ? (
          <div>
            <FieldLabel htmlFor={`${id}-water-issue`} optional>What would you like help with?</FieldLabel>
            <select id={`${id}-water-issue`} name="waterIssue" value={formData.waterIssue} onChange={handleChange} className={fieldClassName}>
              {WATER_ISSUES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
        ) : null}

        {mode === "general" ? (
          <div>
            <FieldLabel htmlFor={`${id}-note`} optional>What can we help you choose?</FieldLabel>
            <textarea id={`${id}-note`} name="note" rows={3} value={formData.note} onChange={handleChange} placeholder="Tell us about your water, space, or product question…" className={fieldClassName} />
          </div>
        ) : null}

        <div className="min-w-0 max-w-full overflow-hidden">
          <div
            id={`${id}-turnstile`}
            ref={turnstileRef}
            tabIndex={-1}
            role="region"
            className="min-h-[66px] w-full min-w-0 max-w-full overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2"
            aria-label="Human verification"
            aria-describedby={turnstileError ? `${id}-turnstile-error` : undefined}
          />
          {!shouldLoadTurnstile ? <p className="text-sm text-water-muted">Secure verification loads when this form is in view.</p> : null}
          <InlineError id={`${id}-turnstile-error`}>{turnstileError}</InlineError>
        </div>

        <div aria-live="polite" aria-atomic="true">
          {status.type === "error" ? (
            <p className="break-words rounded-[0.875rem] border border-red-200 bg-red-50 p-4 text-sm leading-6 text-red-800" role="alert">{status.message}</p>
          ) : null}
        </div>

        <button type="submit" disabled={isSubmitting} className="inline-flex min-h-12 w-full items-center justify-center rounded-[0.875rem] border border-water-blue bg-water-blue px-5 py-3 text-base font-semibold text-white transition-[background-color,border-color] duration-200 hover:border-water-blue-dark hover:bg-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-70 sm:w-auto">
          {isSubmitting ? "Sending request…" : submitLabel || (mode === "business" ? "Request a Business Consultation" : "Request a Free Assessment")}
        </button>
        <p className="text-sm leading-6 text-water-muted">Your information is used only to respond to this request. No obligation and no purchase required.</p>
      </form>
    </>
  );
}
